import React, { useState } from "react";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const branches = [
  {
    id: 1, name: "Downtown Gym", members: 443, revenue: 76400, retention: 87.2, growth: 12.4,
    yoy: 18.6, status: "excellent",
    spark: [{ v: 58 }, { v: 62 }, { v: 59 }, { v: 65 }, { v: 68 }, { v: 72 }, { v: 76 }]
  },
  {
    id: 2, name: "Westside Fitness", members: 312, revenue: 54200, retention: 82.5, growth: 8.1,
    yoy: 11.2, status: "good",
    spark: [{ v: 44 }, { v: 46 }, { v: 48 }, { v: 50 }, { v: 51 }, { v: 53 }, { v: 54 }]
  },
  {
    id: 3, name: "North Park Club", members: 278, revenue: 48700, retention: 79.8, growth: 5.3,
    yoy: 7.4, status: "good",
    spark: [{ v: 40 }, { v: 42 }, { v: 41 }, { v: 44 }, { v: 45 }, { v: 47 }, { v: 49 }]
  },
  {
    id: 4, name: "East End Studio", members: 189, revenue: 31600, retention: 74.1, growth: -2.3,
    yoy: 3.1, status: "warning",
    spark: [{ v: 34 }, { v: 33 }, { v: 35 }, { v: 32 }, { v: 31 }, { v: 32 }, { v: 32 }]
  },
];

const statusConfig = {
  excellent: { label: "Excellent", color: "var(--color-success)", bg: "var(--color-success)" },
  good: { label: "On Track", color: "var(--color-primary)", bg: "var(--color-primary)" },
  warning: { label: "Needs Attention", color: "var(--color-warning)", bg: "var(--color-warning)" },
};

export default function BranchComparisonTable() {
  const [sortKey, setSortKey] = useState("revenue");
  const [sortDir, setSortDir] = useState("desc");
  const [expanded, setExpanded] = useState(false);

  const sorted = [...branches]?.sort((a, b) => sortDir === "desc" ? b?.[sortKey] - a?.[sortKey] : a?.[sortKey] - b?.[sortKey]);
  const displayed = expanded ? sorted : sorted?.slice(0, 3);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  const SortIcon = ({ col }) => (
    <Icon
      name={sortKey === col ? (sortDir === "desc" ? "ChevronDown" : "ChevronUp") : "ChevronsUpDown"}
      size={12}
      color={sortKey === col ? "var(--color-primary)" : "var(--color-muted-foreground)"}
    />
  );

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 md:p-5 border-b border-[var(--color-border)]">
        <div>
          <h3 className="text-sm md:text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">Branch Performance Comparison</h3>
          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">YoY comparisons with trend sparklines</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="xs" iconName="Download" iconPosition="left" iconSize={13}>Export</Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead>
            <tr className="bg-[var(--color-muted)]/50">
              {[
                { key: "name", label: "Branch", sortable: false },
                { key: "members", label: "Members", sortable: true },
                { key: "revenue", label: "Revenue", sortable: true },
                { key: "retention", label: "Retention", sortable: true },
                { key: "growth", label: "Growth", sortable: true },
                { key: "yoy", label: "YoY", sortable: true },
                { key: "trend", label: "Trend", sortable: false },
                { key: "status", label: "Status", sortable: false },
              ]?.map(col => (
                <th
                  key={col?.key}
                  className={`px-4 py-3 text-left text-xs font-semibold text-[var(--color-muted-foreground)] font-[var(--font-caption)] whitespace-nowrap ${col?.sortable ? "cursor-pointer hover:text-[var(--color-foreground)] select-none" : ""}`}
                  onClick={() => col?.sortable && handleSort(col?.key)}
                >
                  <span className="flex items-center gap-1">
                    {col?.label}
                    {col?.sortable && <SortIcon col={col?.key} />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--color-border)]">
            {displayed?.map(branch => {
              const sc = statusConfig?.[branch?.status];
              return (
                <tr key={branch?.id} className="hover:bg-[var(--color-muted)]/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-[var(--radius-sm)] bg-[var(--color-primary)]/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="Building2" size={14} color="var(--color-primary)" />
                      </div>
                      <span className="text-xs font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] whitespace-nowrap">{branch?.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs font-medium text-[var(--color-foreground)] font-[var(--font-data)] whitespace-nowrap">{branch?.members?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs font-medium text-[var(--color-foreground)] font-[var(--font-data)] whitespace-nowrap">${branch?.revenue?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs font-medium text-[var(--color-foreground)] font-[var(--font-data)] whitespace-nowrap">{branch?.retention}%</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`text-xs font-semibold font-[var(--font-data)] ${branch?.growth >= 0 ? "text-[var(--color-success)]" : "text-[var(--color-error)]"}`}>
                      {branch?.growth >= 0 ? "+" : ""}{branch?.growth}%
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-xs font-semibold text-[var(--color-primary)] font-[var(--font-data)]">+{branch?.yoy}%</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="w-20 h-8">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={branch?.spark}>
                          <Line type="monotone" dataKey="v" stroke={branch?.growth >= 0 ? "var(--color-success)" : "var(--color-error)"} strokeWidth={1.5} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium font-[var(--font-caption)] whitespace-nowrap" style={{ backgroundColor: `${sc?.bg}18`, color: sc?.color }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: sc?.color }} />
                      {sc?.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {branches?.length > 3 && (
        <div className="p-3 border-t border-[var(--color-border)] text-center">
          <button onClick={() => setExpanded(e => !e)} className="text-xs text-[var(--color-primary)] font-medium font-[var(--font-caption)] hover:underline flex items-center gap-1 mx-auto">
            <Icon name={expanded ? "ChevronUp" : "ChevronDown"} size={14} color="var(--color-primary)" />
            {expanded ? "Show Less" : `Show All ${branches?.length} Branches`}
          </button>
        </div>
      )}
    </div>
  );
}